import React, { useEffect, useRef, useState } from 'react';
import { LogOut, ShieldCheck, ChevronDown, ChevronUp, User, MessageCircle, Keyboard } from 'lucide-react';
import type { CustomerRecord } from '../CustomerLogin';
import { BRAND_PORTAL } from '../../constants';
import { PortalBadge } from '../arkorbit-ui/PortalBadge';
import { PortalArbeitskontextBar } from './PortalArbeitskontextBar';
import { PortalShortcutHint } from './PortalShortcutHint';
import { PortalAppearanceSwitch } from './PortalAppearanceSwitch';
import { portalShellSurfaceClass } from './portalAppearance';
import { usePortalAppearance } from './portalAppearanceContext';
import { usePortalShortcutMap } from './portalShortcutContext';
import { formatPortalShortcutAria, portalNavShortcutId } from './portalShortcuts';
import { PORTAL_SHELL } from './portalTheme';
import { isBetaTester } from './portalCustomerAccess';
import {
  type PortalView,
  type PortalNavContext,
  getPrimaryNavItems,
  getMehrNavItems,
  getAccountNavItems,
  resolveSidebarActiveView,
  isMehrViewActive,
} from './portalNavigation';

export interface PortalSidebarProps {
  customer: Pick<CustomerRecord, 'kanzlei' | 'email' | 'tier' | 'account_kind'>;
  /** Element am eingeloggten Sitz — ohne Schalter kein Reiter Kanzlei-Chat. */
  elementSeat?: boolean;
  documentCount?: number | null;
  /** Pin neben Akten/Dokumente — kein eigener Portal-View. */
  merkzettelPin?: React.ReactNode;
  /** Sitz-Glocke in der Hauptleiste. */
  sitzGlocke?: React.ReactNode;
  isPremium: boolean;
  contractGeneratorEnabled?: boolean;
  /** MEMCORE Modul B (Labor): Brain unter Mehr (View gedaechtnis), nur Premium und Curator-URL. */
  memcoreEnabled?: boolean;
  teamAccessEnabled?: boolean;
  view: PortalView;
  onNavigate: (view: PortalView) => void;
  onOpenSettings: () => void;
  onLogout: () => void;
  /** Einstellungs-Panel offen — Account-Eintrag hervorheben. */
  settingsOpen?: boolean;
  mehrExpanded?: boolean;
  onMehrExpandedChange?: (open: boolean) => void;
}

export const PortalSidebar: React.FC<PortalSidebarProps> = ({
  customer,
  elementSeat = false,
  documentCount,
  merkzettelPin,
  sitzGlocke,
  isPremium,
  contractGeneratorEnabled = false,
  memcoreEnabled = false,
  teamAccessEnabled = false,
  view,
  onNavigate,
  onOpenSettings,
  onLogout,
  settingsOpen = false,
  mehrExpanded: mehrExpandedProp,
  onMehrExpandedChange,
}) => {
  const [mehrLocal, setMehrLocal] = useState(() => isMehrViewActive(view));
  const [accountOpen, setAccountOpen] = useState(false);
  const accountRef = useRef<HTMLDivElement>(null);
  const mehrExpanded = mehrExpandedProp ?? mehrLocal;
  const setMehrExpanded = onMehrExpandedChange ?? setMehrLocal;

  const shortcutMap = usePortalShortcutMap();
  const light = usePortalAppearance() === 'light';
  const shellMark = portalShellSurfaceClass(light ? 'light' : 'dark');
  const side = light
    ? 'w-[240px] bg-[#e6e1d8] text-[#1c1915] border-r border-[#6a5684] flex flex-col h-full min-h-0 flex-shrink-0'
    : PORTAL_SHELL.sidebar;
  const headerBorder = light ? 'border-b border-[#6a5684]' : PORTAL_SHELL.sidebarHeaderBorder;
  const navActive = light
    ? 'bg-[#f3efe8] text-[#1c1915] ring-1 ring-[#6a5684] border-l-2 border-[#5b4678]'
    : PORTAL_SHELL.sidebarNavActive;
  const navIdle = light
    ? 'text-[#3f3832] hover:text-[#1c1915] hover:bg-[#f3efe8]'
    : PORTAL_SHELL.sidebarNavIdle;
  const hintTone = light ? 'ml-auto !border-[#6a5684] !text-[#3f3832]' : 'ml-auto';
  const activeView = resolveSidebarActiveView(view);
  const betaTester = isBetaTester(customer.account_kind);
  const ctx: PortalNavContext = {
    isPremium,
    contractGeneratorEnabled,
    memcoreEnabled,
    teamAccessEnabled,
    betaTester,
    elementSeat,
  };
  const primary = getPrimaryNavItems(ctx);
  const mehrItems = getMehrNavItems(ctx);
  const accountItems = getAccountNavItems();
  const showDokumenteEditorHint =
    resolveSidebarActiveView(view) === 'dokumente' && view === 'document_editor';
  const accountActive = accountOpen || settingsOpen;

  useEffect(() => {
    if (!accountOpen) return;
    const onPointer = (e: MouseEvent) => {
      if (accountRef.current && !accountRef.current.contains(e.target as Node)) {
        setAccountOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointer);
    return () => document.removeEventListener('mousedown', onPointer);
  }, [accountOpen]);

  const navBtn = (id: PortalView, icon: React.ElementType, label: string, extras?: React.ReactNode) => {
    const Icon = icon;
    const isActive = activeView === id;
    return (
      <button
        key={id}
        type="button"
        data-testid={`portal-nav-${id}`}
        onClick={() => onNavigate(id)}
        className={`${PORTAL_SHELL.sidebarNavItem} ${isActive ? navActive : navIdle}`}
      >
        <Icon className="w-4 h-4 flex-shrink-0 opacity-80" aria-hidden />
        <span className="truncate">{label}</span>
        {extras}
      </button>
    );
  };

  const handleAccountItem = (id: PortalView) => {
    setAccountOpen(false);
    if (id === 'settings') {
      onOpenSettings();
      return;
    }
    onNavigate(id);
  };

  return (
    <div className={`h-full ${side} ${shellMark} flex flex-col`} data-testid="portal-sidebar">
      <div
        className={`px-4 py-4 w-full ${headerBorder} flex items-center gap-2`}
      >
        <button
          type="button"
          onClick={() => onNavigate('home')}
          className={`min-w-0 flex-1 text-left flex items-center gap-3 ${navIdle} ${light ? '' : 'hover:bg-white/[0.03]'} transition-colors rounded-lg -ml-1 pl-1 py-0.5`}
          aria-label="Zur Startseite"
          title="Start"
        >
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0 relative ${light ? 'border border-[#6a5684] bg-[#f3efe8]' : 'border border-white/[0.08] bg-slate-900/80'}`}>
            <img
              src="/arkorbit-wappen.png"
              alt="ARKORBIT"
              className="absolute inset-0 w-full h-full object-cover object-center"
              style={{ transform: 'scale(1.9) translateY(23%)' }}
            />
          </div>
          <div className="min-w-0">
            <p className={`${light ? 'text-[#1c1915]' : 'text-white'} font-semibold text-sm leading-tight truncate`}>{BRAND_PORTAL}</p>
            <p className={`${light ? 'text-[#3f3832]' : 'text-slate-500'} text-[10px] font-medium tracking-wide truncate`}>Arbeitsplatz</p>
          </div>
        </button>
        {sitzGlocke}
      </div>

      <div className="px-2 pt-2">
        <PortalAppearanceSwitch />
      </div>

      <nav className="flex-1 min-h-0 px-2 py-3 space-y-0.5 overflow-y-auto custom-scrollbar">
        {primary.map((item) => {
          const Icon = item.icon;
          const isActive = activeView === item.id;
          const shortcutId = portalNavShortcutId(item.id);
          const shortcutAria = shortcutId ? formatPortalShortcutAria(shortcutMap[shortcutId]) : undefined;
          return (
            <button
              key={item.id}
              type="button"
              data-testid={`portal-nav-${item.id}`}
              onClick={() => onNavigate(item.id)}
              title={shortcutAria}
              aria-keyshortcuts={shortcutAria}
              className={`${PORTAL_SHELL.sidebarNavItem} ${isActive ? navActive : navIdle}`}
            >
              <Icon className="w-4 h-4 flex-shrink-0 opacity-80" aria-hidden />
              <span className="truncate">{item.label}</span>
              {shortcutId ? <PortalShortcutHint id={shortcutId} className={hintTone} /> : null}
              {item.badge === 'beta' ? (
                <PortalBadge label="β" accent="amber" className="ml-auto !px-1.5 !py-0 text-[9px]" />
              ) : null}
            </button>
          );
        })}

        {merkzettelPin ? <div className="pt-0.5">{merkzettelPin}</div> : null}

        {showDokumenteEditorHint && (
          <p className={`mx-2 mt-2 px-2 py-2 text-[10px] leading-relaxed border-l ${light ? 'text-[#3f3832] border-[#6a5684]' : 'text-slate-500 border-white/[0.06]'}`}>
            ARKO erscheint rechts, sobald Sie ein Dokument im Editor öffnen.
          </p>
        )}

        <div className="pt-2">
          <button
            type="button"
            data-testid="portal-nav-mehr"
            onClick={() => setMehrExpanded(!mehrExpanded)}
            className={`${PORTAL_SHELL.sidebarNavItem} w-full ${
              isMehrViewActive(view) ? navActive : navIdle
            }`}
            aria-expanded={mehrExpanded}
          >
            <ChevronDown
              className={`w-4 h-4 flex-shrink-0 transition-transform ${mehrExpanded ? 'rotate-0' : '-rotate-90'}`}
              aria-hidden
            />
            <span>Mehr</span>
          </button>
          {mehrExpanded && (
            <div className={`mt-0.5 ml-2 pl-2 border-l space-y-0.5 ${light ? 'border-[#6a5684]' : 'border-white/[0.06]'}`}>
              {mehrItems.map((item) => {
                const extras = (
                  <>
                    {item.dot && (
                      <span className="ml-auto w-1.5 h-1.5 rounded-full bg-amber-400/80" aria-hidden />
                    )}
                    {item.badge === 'neu' && (
                      <PortalBadge label="neu" accent="violet" className="ml-auto !px-1.5 !py-0 text-[9px]" />
                    )}
                    {item.badge === 'beta' && (
                      <PortalBadge label="α" accent="amber" className="ml-auto !px-1.5 !py-0 text-[9px]" />
                    )}
                  </>
                );
                return navBtn(item.id, item.icon, item.label, extras);
              })}
            </div>
          )}
        </div>
      </nav>

      <div className={`portal-sidebar-footer flex flex-col gap-2 px-2.5 py-2.5 border-t flex-shrink-0 min-w-0 overflow-visible ${light ? 'border-[#6a5684]' : 'border-white/[0.06]'}`}>
        <button
          type="button"
          data-testid="portal-nav-tasten"
          onClick={() => onNavigate('tasten')}
          className={`${PORTAL_SHELL.sidebarNavItem} ${
            activeView === 'tasten' ? navActive : navIdle
          }`}
        >
          <Keyboard className="w-4 h-4 flex-shrink-0 opacity-80" aria-hidden />
          <span className="truncate">Tasten</span>
        </button>
        {betaTester ? (
          <button
            type="button"
            data-testid="portal-nav-feedback"
            onClick={() => onNavigate('feedback')}
            className={`${PORTAL_SHELL.sidebarNavItem} ${
              activeView === 'feedback' ? navActive : navIdle
            } ring-1 ring-amber-400/30 bg-amber-500/[0.06]`}
          >
            <MessageCircle className="w-4 h-4 flex-shrink-0 opacity-80" aria-hidden />
            <span className="truncate">Beta-Feedback</span>
            <PortalBadge label="β" accent="amber" className="ml-auto !px-1.5 !py-0 text-[9px]" />
          </button>
        ) : null}
        <PortalArbeitskontextBar
          customer={customer}
          documentCount={documentCount}
          placement="sidebar-footer"
          className="!p-0"
        />
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 min-w-0">
          <span className={`inline-flex items-center gap-1 text-[11px] font-medium uppercase whitespace-nowrap ${light ? 'text-[#0c6b4a]' : 'text-emerald-500/80'}`}>
            <ShieldCheck className="w-3.5 h-3.5 flex-shrink-0" aria-hidden /> EU · Frankfurt
          </span>
        </div>

        <div ref={accountRef} className="relative shrink-0 px-1" data-testid="portal-account-status">
          {accountOpen && (
            <div
              className={`absolute bottom-full left-0 right-0 mb-1 max-h-56 overflow-y-auto rounded-lg border shadow-xl z-10 ${light ? 'border-[#6a5684] bg-[#f3efe8]' : 'border-violet-400/30 bg-[#07070a]'}`}
              role="menu"
              aria-label="Konto & Sicherheit"
            >
              <div className={`px-3 py-2 border-b ${light ? 'border-[#6a5684]' : 'border-violet-400/20'}`}>
                <p className={`text-[11px] font-semibold uppercase tracking-wider ${light ? 'text-[#5b4678]' : 'text-violet-200/80'}`}>
                  Konto & Sicherheit
                </p>
                <p className={`${light ? 'text-[#1c1915]' : 'text-slate-100'} text-xs font-medium truncate mt-0.5`}>{customer.kanzlei}</p>
                <p className={`${light ? 'text-[#3f3832]' : 'text-slate-400'} text-[10px] truncate`}>{customer.email}</p>
              </div>
              <div className="py-1">
                {accountItems.map((item) => {
                  const Icon = item.icon;
                  const isSettings = item.id === 'settings';
                  const isActive = isSettings && settingsOpen;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      role="menuitem"
                      data-testid={`portal-nav-${item.id}`}
                      onClick={() => handleAccountItem(item.id)}
                      className={`w-full flex items-center gap-2.5 px-3 py-1.5 text-left text-sm transition-colors ${
                        isActive
                          ? light
                            ? 'bg-[#e6e1d8] text-[#1c1915]'
                            : 'bg-violet-500/15 text-white'
                          : light
                            ? 'text-[#1c1915] hover:bg-[#e6e1d8]'
                            : 'text-slate-200 hover:bg-violet-500/10 hover:text-white'
                      }`}
                    >
                      <Icon className="w-4 h-4 flex-shrink-0 opacity-80" aria-hidden />
                      <span className="truncate">{item.label}</span>
                    </button>
                  );
                })}
              </div>
              <div className={`border-t py-1 ${light ? 'border-[#6a5684]' : 'border-violet-400/20'}`}>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setAccountOpen(false);
                    onLogout();
                  }}
                  className={`w-full flex items-center gap-2.5 px-3 py-1.5 text-left text-sm transition-colors ${light ? 'text-[#8f2d2d] hover:bg-[#e6e1d8]' : 'text-red-300/90 hover:bg-red-500/10 hover:text-red-200'}`}
                >
                  <LogOut className="w-4 h-4 flex-shrink-0 opacity-80" aria-hidden />
                  <span>Abmelden</span>
                </button>
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={() => setAccountOpen((o) => !o)}
            className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg border text-left transition-colors overflow-hidden ${
              accountActive
                ? light
                  ? 'border-[#5b4678] bg-[#f3efe8] text-[#1c1915]'
                  : 'border-violet-400/50 bg-[#07070a] text-white'
                : light
                  ? 'border-[#6a5684] bg-[#f3efe8] text-[#1c1915] hover:border-[#5b4678]'
                  : 'border-violet-400/30 bg-[#07070a] text-slate-100 hover:border-violet-400/50'
            }`}
            aria-expanded={accountOpen}
            aria-haspopup="menu"
            aria-label="Konto & Sicherheit"
          >
            <div className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${light ? 'bg-[#f3efe8] border border-[#6a5684]' : 'bg-[#07070a] border border-violet-400/30'}`}>
              <User className={`w-3.5 h-3.5 ${light ? 'text-[#5b4678]' : 'text-violet-200'}`} aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <p className={`${light ? 'text-[#1c1915]' : 'text-slate-100'} text-xs font-medium leading-tight truncate`}>{customer.kanzlei}</p>
              <p className={`${light ? 'text-[#3f3832]' : 'text-slate-400'} text-[11px] leading-tight truncate`}>Konto & Sicherheit</p>
            </div>
            {accountOpen ? (
              <ChevronUp className={`w-3.5 h-3.5 flex-shrink-0 ${light ? 'text-[#5b4678]' : 'text-violet-200/70'}`} aria-hidden />
            ) : (
              <ChevronDown className={`w-3.5 h-3.5 flex-shrink-0 ${light ? 'text-[#5b4678]' : 'text-violet-200/70'}`} aria-hidden />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
